import { Component, OnInit, OnDestroy, Inject, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject, takeUntil, debounceTime, switchMap, of, catchError, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ActivityScheduleService } from '../../services/activity-schedule.service';
import { ScheduledActivity, ActivityType, ConflictCheckResult } from '../../interfaces/activity-schedule.interface';
import { NotificationService } from 'src/app/services/notification.service';
import { AuthService } from 'src/app/services/auth.service';

export interface ActivityBookingData {
  cerereId?: number;
  selectedDate?: string;
  selectedTime?: string;
  prefilledData?: Partial<ScheduledActivity>;
  mode?: 'create' | 'edit' | 'reschedule';
  activityId?: number;
}

export interface TimeSlot {
  start: string;
  end: string;
  available: boolean;
  conflicts?: ConflictCheckResult[];
}

@Component({
  selector: 'app-activity-booking',
  templateUrl: './activity-booking.component.html',
  styleUrls: ['./activity-booking.component.scss']
})
export class ActivityBookingComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  // State management
  isLoading = false;
  isSaving = false;
  isCheckingAvailability = false;
  mode: 'create' | 'edit' | 'reschedule' = 'create';
  
  // Data
  activityTypes: ActivityType[] = [];
  availableTimeSlots: TimeSlot[] = [];
  suggestedTimes: string[] = [];
  conflicts: ConflictCheckResult[] = [];
  
  // Form
  bookingForm!: FormGroup;
  
  // Configuration
  timeSlots: string[] = [];
  durationOptions = [
    { value: 15, label: '15 minute' },
    { value: 30, label: '30 minute' },
    { value: 45, label: '45 minute' },
    { value: 60, label: '1 oră' },
    { value: 90, label: '1.5 ore' },
    { value: 120, label: '2 ore' },
    { value: 180, label: '3 ore' },
    { value: 240, label: '4 ore' }
  ];
  
  priorityOptions = [
    { value: 'low', label: 'Scăzută', color: '#4CAF50' },
    { value: 'medium', label: 'Medie', color: '#2196F3' },
    { value: 'high', label: 'Înaltă', color: '#FF9800' },
    { value: 'urgent', label: 'Urgentă', color: '#F44336' }
  ];

  // UI State
  showAdvancedOptions = false;
  showConflictResolution = false;
  selectedDate: Date = new Date();
  minDate: Date = new Date();
  maxDate: Date = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000); // 90 days from now

  constructor(
    private fb: FormBuilder,
    private activityScheduleService: ActivityScheduleService,
    private notificationService: NotificationService,
    private authService: AuthService,
    @Optional() private dialogRef: MatDialogRef<ActivityBookingComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: ActivityBookingData
  ) {
    this.mode = data?.mode || 'create';
  }

  ngOnInit(): void {
    this.generateTimeSlots();
    this.initializeForm();
    this.setupFormListeners();
    
    // Load activity types first, then populate initial data
    this.loadActivityTypes().then(() => {
      if (this.data) {
        this.populateInitialData();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private generateTimeSlots(): void {
    for (let hour = 6; hour <= 22; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        this.timeSlots.push(timeStr);
      }
    }
  }

  private initializeForm(): void {
    this.bookingForm = this.fb.group({
      // Basic Information
      activityTypeId: [null, [Validators.required]],
      title: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(255)]],
      description: ['', [Validators.maxLength(1000)]],
      
      // Date and Time
      date: [this.data?.selectedDate ? new Date(this.data.selectedDate) : new Date(), [Validators.required, this.futureDateValidator]],
      startTime: [this.ensureTimeFormat(this.data?.selectedTime || '09:00'), [Validators.required]],
      duration: [60, [Validators.required, Validators.min(15), Validators.max(480)]],
      endTime: [{ value: this.ensureTimeFormat('10:00'), disabled: true }],
      
      // Location and Details
      location: ['', [Validators.maxLength(255)]],
      isOnline: [false],
      onlineLink: [''],
      
      // Assignment
      assignedUserId: [null],
      assignedFunctieId: [null],
      assignedDirectieId: [null],
      assignedCompartimentId: [null],
      
      // Priority and Settings
      priority: ['medium'],
      isPrivate: [false],
      allowConflicts: [false],
      sendNotifications: [true],
      
      // Integration
      cerereId: [this.data?.cerereId || null],
      
      // Notes
      internalNotes: ['', [Validators.maxLength(500)]],
      participantNotes: ['', [Validators.maxLength(500)]]
    });
  }

  private setupFormListeners(): void {
    // Auto-calculate end time when start time or duration changes
    this.bookingForm.get('startTime')?.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => this.updateEndTime());

    this.bookingForm.get('duration')?.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => this.updateEndTime());

    // Check availability when date, time, or duration changes
    this.bookingForm.valueChanges.pipe(
      debounceTime(500),
      switchMap(() => this.checkAvailabilityAndConflicts()),
      takeUntil(this.destroy$)
    ).subscribe();

    // Online meeting link validation
    this.bookingForm.get('isOnline')?.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(isOnline => {
      const linkControl = this.bookingForm.get('onlineLink');
      if (isOnline) {
        linkControl?.setValidators([Validators.required, Validators.pattern(/^https?:\/\/.+/)]);
      } else {
        linkControl?.clearValidators();
      }
      linkControl?.updateValueAndValidity();
    });

    // Activity type change - update title suggestion
    this.bookingForm.get('activityTypeId')?.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(typeId => {
      if (typeId && !this.bookingForm.get('title')?.value) {
        const activityType = this.activityTypes.find(t => t.id === typeId);
        if (activityType) {
          this.bookingForm.get('title')?.setValue(activityType.name);
        }
      }
    });
  }

  private loadActivityTypes(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.isLoading = true;
      this.activityScheduleService.getActivityTypes().pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: (types) => {
          this.activityTypes = types;
          this.isLoading = false;
          resolve();
        },
        error: (error) => {
          console.error('Error loading activity types:', error);
          this.notificationService.warningSwal('Eroare', 'Eroare la încărcarea tipurilor de activități', 'error');
          this.isLoading = false;
          reject(error);
        }
      });
    });
  }

  private populateInitialData(): void {
    console.log('Populating initial data with:', this.data);
    console.log('Available activity types:', this.activityTypes);
    
    if (this.data?.prefilledData) {
      const prefilled = this.data.prefilledData;
      console.log('Prefilled data:', prefilled);
      
      // Ensure start time is properly formatted
      let startTime = '9:00';
      if (prefilled.start_time) {
        startTime = this.ensureTimeFormat(prefilled.start_time);
        console.log('Start time formatted from', prefilled.start_time, 'to', startTime);
      }
      
      // Validate activity type exists
      let activityTypeId = prefilled.activity_type_id || null;
      if (activityTypeId && this.activityTypes.length > 0) {
        const typeExists = this.activityTypes.some(type => type.id === activityTypeId);
        if (!typeExists) {
          console.warn('Activity type ID', activityTypeId, 'not found in available types');
          activityTypeId = null;
        }
      }
      
      // Patch all the values at once
      const formData = {
        activityTypeId: activityTypeId,
        title: prefilled.title || '',
        description: prefilled.description || '',
        date: prefilled.scheduled_date ? new Date(prefilled.scheduled_date) : new Date(),
        startTime: startTime,
        duration: prefilled.duration_minutes || 60,
        location: prefilled.location || '',
        assignedUserId: prefilled.assigned_user_id || null,
        assignedFunctieId: prefilled.assigned_functie_id || null,
        assignedDirectieId: prefilled.assigned_directie_id || null,
        assignedCompartimentId: prefilled.assigned_compartiment_id || null,
        priority: 'medium', // Use default since this field doesn't exist in ScheduledActivity
        isPrivate: false, // Use default since this field doesn't exist in ScheduledActivity
        sendNotifications: true, // Use default since this field doesn't exist in ScheduledActivity
        cerereId: prefilled.cerere_id || null,
        internalNotes: prefilled.internal_notes || '', // Use internal_notes field
        participantNotes: prefilled.participant_notes || '' // Use participant_notes field
      };
      
      console.log('Form data to patch:', formData);
      
      // Use setTimeout to ensure the form is properly initialized
      setTimeout(() => {
        this.bookingForm.patchValue(formData);
        
        // Manually trigger end time calculation after a short delay
        setTimeout(() => {
          this.updateEndTime();
        }, 50);
        
        // Mark form as dirty to trigger validation
        setTimeout(() => {
          this.bookingForm.updateValueAndValidity();
          console.log('Form patched. Current values:', this.bookingForm.value);
          
          // Log specific field values for debugging
          console.log('Activity Type ID:', this.bookingForm.get('activityTypeId')?.value);
          console.log('Start Time:', this.bookingForm.get('startTime')?.value);
          console.log('Location:', this.bookingForm.get('location')?.value);
        }, 100);
      }, 50);
    }

    // Handle direct data parameters (for new activities)
    if (this.data?.selectedDate && !this.data?.prefilledData?.scheduled_date) {
      this.selectedDate = new Date(this.data.selectedDate);
      setTimeout(() => {
        this.bookingForm.get('date')?.setValue(this.selectedDate);
      }, 50);
    }

    if (this.data?.selectedTime && !this.data?.prefilledData?.start_time) {
      setTimeout(() => {
        this.bookingForm.get('startTime')?.setValue(this.ensureTimeFormat(this.data.selectedTime || '9:00'));
      }, 50);
    }
  }

  // Validators
  private futureDateValidator = (control: AbstractControl) => {
    if (!control.value) return null;
    
    const selectedDate = new Date(control.value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      return { pastDate: true };
    }
    
    return null;
  };

  // Time Management
  private updateEndTime(): void {
    const startTime = this.bookingForm.get('startTime')?.value;
    const duration = this.bookingForm.get('duration')?.value;
    
    if (startTime && duration) {
      const [hours, minutes] = startTime.split(':').map(Number);
      const startMinutes = hours * 60 + minutes;
      const endMinutes = startMinutes + duration;
      
      if (endMinutes >= 24 * 60) {
        // Activity spans to next day
        this.bookingForm.get('endTime')?.setValue('23:59');
        this.bookingForm.get('duration')?.setErrors({ exceedsDay: true });
      } else {
        const endHours = Math.floor(endMinutes / 60);
        const endMins = endMinutes % 60;
        const endTime = this.ensureTimeFormat(`${endHours}:${endMins.toString().padStart(2, '0')}`);
        this.bookingForm.get('endTime')?.setValue(endTime);
        
        // Clear duration error if it was set
        const durationControl = this.bookingForm.get('duration');
        if (durationControl?.hasError('exceedsDay')) {
          const errors = { ...durationControl.errors };
          delete errors['exceedsDay'];
          durationControl.setErrors(Object.keys(errors).length ? errors : null);
        }
      }
    }
  }

  private checkAvailabilityAndConflicts(): Observable<any> {
    const date = this.bookingForm.get('date')?.value;
    const startTime = this.bookingForm.get('startTime')?.value;
    const duration = this.bookingForm.get('duration')?.value;
    const assignedUserId = this.bookingForm.get('assignedUserId')?.value;
    
    if (!date || !startTime || !duration) {
      return of(null);
    }
    
    this.isCheckingAvailability = true;
    
    const dateStr = date.toISOString().split('T')[0];
    const endTime = this.bookingForm.get('endTime')?.value;
    const excludeActivityId = this.data?.activityId;
    
    // Ensure times are in proper format
    const formattedStartTime = this.ensureTimeFormat(startTime);
    const formattedEndTime = this.ensureTimeFormat(endTime);
    
    // Additional validation to ensure we have valid times
    if (!formattedStartTime || !formattedEndTime) {
      console.warn('Invalid time format detected, skipping conflict check');
      this.isCheckingAvailability = false;
      return of(null);
    }

    // Use current user ID if no assigned user (for conflict checking)
    const userIdForCheck = assignedUserId || this.getCurrentUserId();
    
    const requestPayload = {
      user_id: userIdForCheck,
      scheduled_date: dateStr,
      start_time: formattedStartTime,
      end_time: formattedEndTime,
      exclude_activity_id: excludeActivityId
    };
    
    console.log('Conflict check request payload:', requestPayload);
    
    // Return the observable and handle the subscription in the caller
    return this.activityScheduleService.checkConflicts(
      userIdForCheck,
      dateStr,
      formattedStartTime,
      formattedEndTime,
      excludeActivityId
    ).pipe(
      catchError(error => {
        console.error('Error checking conflicts:', error);
        this.isCheckingAvailability = false;
        return of([]);
      }),
      map((result: any) => {
        this.conflicts = Array.isArray(result) ? result : [];
        this.isCheckingAvailability = false;
        this.updateAvailabilityStatus();
        return result;
      })
    );
  }

  private updateAvailabilityStatus(): void {
    const hasConflicts = this.conflicts.length > 0;
    const allowConflicts = this.bookingForm.get('allowConflicts')?.value;
    
    this.showConflictResolution = hasConflicts && !allowConflicts;
    
    if (hasConflicts) {
      this.generateSuggestedTimes();
    }
  }

  private generateSuggestedTimes(): void {
    const date = this.bookingForm.get('date')?.value;
    const duration = this.bookingForm.get('duration')?.value;
    
    if (!date || !duration) return;
    
    const dateStr = date.toISOString().split('T')[0];
    
    // For now, generate simple time suggestions
    const suggestions: string[] = [];
    const startHour = 8;
    const endHour = 18;
    
    for (let hour = startHour; hour <= endHour - Math.ceil(duration / 60); hour++) {
      suggestions.push(`${hour.toString().padStart(2, '0')}:00`);
      suggestions.push(`${hour.toString().padStart(2, '0')}:30`);
      if (suggestions.length >= 5) break;
    }
    
    this.suggestedTimes = suggestions;
  }

  // UI Helpers
  getModalTitle(): string {
    switch (this.mode) {
      case 'edit': return 'Editează Activitatea';
      case 'reschedule': return 'Reprogramează Activitatea';
      default: return 'Activitate Nouă';
    }
  }

  getModalIcon(): string {
    switch (this.mode) {
      case 'edit': return 'edit';
      case 'reschedule': return 'schedule';
      default: return 'add_circle';
    }
  }

  getSubmitButtonText(): string {
    if (this.isSaving) return 'Se salvează...';
    
    switch (this.mode) {
      case 'edit': return 'Actualizează Activitatea';
      case 'reschedule': return 'Reprogramează';
      default: return 'Creează Activitatea';
    }
  }

  hasConflicts(): boolean {
    return this.conflicts.length > 0;
  }

  getConflictMessage(): string {
    if (this.conflicts.length === 0) return '';
    
    const conflictTypes = [...new Set(this.conflicts.map(c => c.type))];
    
    if (conflictTypes.includes('availability')) {
      return 'Utilizatorul nu este disponibil în această perioadă';
    }
    
    if (conflictTypes.includes('conflict')) {
      return `Există ${this.conflicts.length} activitate${this.conflicts.length > 1 ? 'ăți' : ''} programată${this.conflicts.length > 1 ? 'e' : ''} în acest interval`;
    }
    
    if (conflictTypes.includes('notice')) {
      return 'Perioada de notificare minimă nu este respectată';
    }
    
    return `${this.conflicts.length} conflict${this.conflicts.length > 1 ? 'e' : ''} detectat${this.conflicts.length > 1 ? 'e' : ''}`;
  }

  getPriorityColor(priority: string): string {
    const option = this.priorityOptions.find(p => p.value === priority);
    return option?.color || '#2196F3';
  }

  // Actions
  applySuggestedTime(time: string): void {
    this.bookingForm.get('startTime')?.setValue(time);
  }

  toggleAdvancedOptions(): void {
    this.showAdvancedOptions = !this.showAdvancedOptions;
  }

  forceBooking(): void {
    this.bookingForm.get('allowConflicts')?.setValue(true);
    this.showConflictResolution = false;
  }

  onSave(): void {
    if (this.bookingForm.invalid) {
      this.notificationService.warningSwal('Eroare', 'Vă rugăm să completați toate câmpurile obligatorii', 'error');
      this.markFormGroupTouched(this.bookingForm);
      return;
    }

    if (this.hasConflicts() && !this.bookingForm.get('allowConflicts')?.value) {
      this.notificationService.warningSwal(
        'Conflict detectat', 
        'Există conflicte de programare. Doriți să forțați rezervarea sau să alegeți o altă oră?',
        'warning'
      );
      return;
    }

    this.isSaving = true;
    const formValue = this.bookingForm.value;
    
    // Map camelCase form fields to snake_case API fields
    const activityData = {
      activity_type_id: formValue.activityTypeId,
      title: formValue.title || '',
      description: formValue.description || '',
      scheduled_date: this.formatDateWithoutTimezone(formValue.date),
      start_time: formValue.startTime,
      end_time: this.bookingForm.get('endTime')?.value || formValue.endTime,
      duration_minutes: formValue.duration,
      location: formValue.location || '',
      is_online: formValue.isOnline || false,
      online_link: formValue.onlineLink || '',
      assigned_user_id: formValue.assignedUserId || this.getCurrentUserId(), // Auto-assign to creator if no one assigned
      assigned_functie_id: formValue.assignedFunctieId || null,
      assigned_directie_id: formValue.assignedDirectieId || null,
      assigned_compartiment_id: formValue.assignedCompartimentId || null,
      priority: formValue.priority || 'medium',
      is_private: formValue.isPrivate || false,
      allow_conflicts: formValue.allowConflicts || false,
      send_notifications: formValue.sendNotifications !== false,
      cerere_id: formValue.cerereId || null,
      internal_notes: formValue.internalNotes || '',
      participant_notes: formValue.participantNotes || '',
      created_by_user_id: this.getCurrentUserId()
    };

    console.log('Activity data being sent:', activityData);
    console.log('Current user ID:', this.getCurrentUserId());
    console.log('Assigned user ID will be:', activityData.assigned_user_id);

    // Choose the appropriate service method
    let saveOperation;
    
    if (this.mode === 'edit' && this.data?.activityId) {
      // Update existing activity
      saveOperation = this.activityScheduleService.updateActivity(this.data.activityId, activityData);
    } else {
      // Create new activity
      saveOperation = this.activityScheduleService.createActivity(activityData);
    }

    saveOperation.pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (savedActivity: any) => {
        this.isSaving = false;
        
        const message = this.mode === 'edit' 
          ? 'Activitatea a fost actualizată cu succes!' 
          : 'Activitatea a fost creată cu succes!';
          
        this.notificationService.successSwal('Succes', message);
        
        if (this.dialogRef) {
          this.dialogRef.close(savedActivity);
        }
      },
      error: (error: any) => {
        this.isSaving = false;
        console.error('Error saving activity:', error);
        
        const message = this.mode === 'edit'
          ? 'Eroare la actualizarea activității'
          : 'Eroare la crearea activității';
          
        this.notificationService.warningSwal('Eroare', message, 'error');
      }
    });
  }

  onCancel(): void {
    if (this.bookingForm.dirty) {
      this.notificationService.warningSwal(
        'Confirmare',
        'Aveți modificări nesalvate. Doriți să închideți formularul?',
        'warning'
      ).then((result) => {
        if (result.isConfirmed) {
          if (this.dialogRef) {
            this.dialogRef.close();
          }
        }
      });
    } else {
      if (this.dialogRef) {
        this.dialogRef.close();
      }
    }
  }

  // Form Validation Helpers
  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  getFieldError(fieldName: string): string {
    const field = this.bookingForm.get(fieldName);
    if (!field?.errors || !field.touched) return '';

    if (field.hasError('required')) return `${this.getFieldLabel(fieldName)} este obligatoriu`;
    if (field.hasError('minlength')) return `${this.getFieldLabel(fieldName)} este prea scurt`;
    if (field.hasError('maxlength')) return `${this.getFieldLabel(fieldName)} este prea lung`;
    if (field.hasError('pattern')) return `Format invalid pentru ${this.getFieldLabel(fieldName)}`;
    if (field.hasError('pastDate')) return 'Nu puteți programa activități în trecut';
    if (field.hasError('exceedsDay')) return 'Activitatea nu poate depăși miezul nopții';
    if (field.hasError('min')) return `Valoarea minimă este ${field.errors?.['min']?.min}`;
    if (field.hasError('max')) return `Valoarea maximă este ${field.errors?.['max']?.max}`;

    return 'Câmp invalid';
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      'activityTypeId': 'Tipul activității',
      'title': 'Titlul',
      'description': 'Descrierea',
      'date': 'Data',
      'startTime': 'Ora de început',
      'duration': 'Durata',
      'location': 'Locația',
      'onlineLink': 'Link-ul online',
      'priority': 'Prioritatea'
    };
    
    return labels[fieldName] || fieldName;
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.bookingForm.get(fieldName);
    return !!(field?.invalid && field?.touched);
  }

  private getCurrentUserId(): number {
    // Try to get user data synchronously first
    const user = this.authService.getCurrentUserSync();
    console.log('Current user data for ID extraction:', user);
    
    if (user && typeof user === 'object') {
      const userId = user['id_user'] || user['id'];
      console.log('Extracted user ID:', userId);
      if (userId) {
        return parseInt(userId, 10);
      }
    }
    
    // Fallback - but this should be avoided
    console.warn('Could not get current user ID, using fallback. User data:', user);
    return 1;
  }

  /**
   * Format date without timezone conversion to preserve the selected date
   */
  private formatDateWithoutTimezone(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Ensure time is in H:i format (single digit hour, double digit minute)
   * Laravel's H:i format: 0-23 for hours (no leading zero for single digits), 00-59 for minutes (always two digits)
   */
  private ensureTimeFormat(timeString: string): string {
    if (!timeString) {
      return '9:00'; // Default fallback
    }
    
    let timeToFormat: string;
    
    // Handle different time formats from backend
    if (timeString.includes('T') && timeString.includes('Z')) {
      // ISO datetime string like "2025-08-13T07:00:00.000000Z"
      const date = new Date(timeString);
      if (isNaN(date.getTime())) {
        console.error('Invalid datetime string:', timeString);
        return '9:00';
      }
      const hours = date.getHours();
      const minutes = date.getMinutes();
      timeToFormat = `${hours}:${minutes.toString().padStart(2, '0')}`;
    } else if (timeString.includes(':')) {
      // Time string like "07:00" or "07:00:00"
      timeToFormat = timeString.split(':').slice(0, 2).join(':'); // Remove seconds if present
    } else {
      return '9:00';
    }
    
    const [hours, minutes] = timeToFormat.split(':');
    if (!hours || !minutes) {
      return '9:00';
    }
    
    const hourNum = parseInt(hours, 10);
    const minNum = parseInt(minutes, 10);
    
    if (isNaN(hourNum) || isNaN(minNum) || hourNum < 0 || hourNum > 23 || minNum < 0 || minNum > 59) {
      console.error('Invalid time values:', { hours, minutes, hourNum, minNum });
      return '9:00';
    }
    
    // Format as H:i (single digit hour for 0-9, double digit minute always)
    const formattedTime = `${hourNum}:${minNum.toString().padStart(2, '0')}`;
    
    return formattedTime;
  }

}